import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Send, Loader2, ArrowLeft, Sparkles, CheckCircle2, Briefcase, Mic, Paperclip, FileText, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ORB = '/cloudgm-ai-orb.png';

const chatStyles = `
@keyframes agFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
@keyframes agFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes agPing { 0% { transform: scale(1); opacity: 0.7; } 80%,100% { transform: scale(1.9); opacity: 0; } }
@keyframes agDot { 0%,80%,100% { transform: translateY(0); opacity: 0.5; } 40% { transform: translateY(-5px); opacity: 1; } }
.ag-msg-in { animation: agFadeIn 0.35s ease both; }
.ag-orb-float { animation: agFloat 3s ease-in-out infinite; }
.ag-ping-ring { animation: agPing 2.5s cubic-bezier(0,0,0.2,1) infinite; }
.ag-think-dot { animation: agDot 1.2s ease-in-out infinite; box-shadow: 0 0 8px rgba(30,155,217,0.8); }
.ag-md p { margin: 0 0 0.5em; } .ag-md p:last-child { margin-bottom: 0; }
.ag-md ul, .ag-md ol { margin: 0.25em 0 0.5em; padding-inline-start: 1.25em; }
.ag-md li { margin-bottom: 0.2em; }
.ag-md strong { color: #1E9BD9; }
.ag-md h1, .ag-md h2, .ag-md h3 { font-weight: 700; margin: 0.5em 0 0.25em; font-size: 1em; }
.ag-md code { background: rgba(30,155,217,0.12); padding: 0.1em 0.35em; border-radius: 4px; font-size: 0.85em; direction: ltr; display: inline-block; }
`;

export default function AgentTaskChat() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { taskId } = useParams();

  const [sessionId] = useState(() => `agtsess_${uuidv4()}`);
  const [task, setTask] = useState(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Attachment (image or PDF)
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  // Fetch task details
  useEffect(() => {
    const fetchTask = async () => {
      const token = localStorage.getItem('business_token');
      if (!token || !taskId) return;

      // Special sentinel: generic "fetch companies" — no admin task doc.
      if (taskId === 'fetch-companies') {
        setTask({
          id: 'fetch-companies',
          title_ar: 'جلب الشركات',
          title_en: 'Fetch Companies',
          description_ar: 'جمع بيانات شركة جديدة لإضافتها إلى المنصة',
          description_en: 'Collect data for a new company to add to the platform',
          ai_agent_id: null,
        });
        setMessages([{
          role: 'assistant',
          time: new Date(),
          content: language === 'ar'
            ? 'مرحباً 👋\n\nأنا مساعدك الذكي لتسجيل شركات جديدة على منصة **CloudGM Business**.\n\nسأسألك خطوة بخطوة عن بيانات الشركة، ثم أعرض لك ملخصاً كاملاً قبل الإرسال.\n\nلنبدأ — **ما اسم الشركة التي تريد إضافتها؟**'
            : 'Hi 👋\n\nI\'m your AI assistant for registering new companies on **CloudGM Business**.\n\nI\'ll walk you through gathering the company details, then show you a clean summary before submitting.\n\nLet\'s start — **what is the name of the company you want to add?**'
        }]);
        setTaskLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/agent/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTask(data);
        const title = language === 'ar'
          ? (data.title_ar || data.title || 'مهمة الوكيل')
          : (data.title_en || data.title_ar || 'Agent Task');
        setMessages([{
          role: 'assistant',
          time: new Date(),
          content: language === 'ar'
            ? `مرحباً 👋\n\nأنا مساعدك الذكي لمهمة **"${title}"**.\n\nسأرشدك خطوة بخطوة لتجميع كل المعلومات المطلوبة. عندما ننتهي سأعرض لك ملخصاً كاملاً قبل إرسال الطلب للمراجعة.\n\nلنبدأ 🚀`
            : `Hi 👋\n\nI'm your AI assistant for the task **"${title}"**.\n\nI'll walk you through gathering all the required info step by step. When we're done, I'll show you a full summary before submitting for review.\n\nLet's start 🚀`
        }]);
      } catch (err) {
        console.error('Failed to fetch task', err);
        toast.error(language === 'ar' ? 'فشل تحميل المهمة' : 'Failed to load task');
      } finally {
        setTaskLoading(false);
      }
    };
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [booting, taskLoading]);

  const sendMessage = async () => {
    if ((!input.trim() && !attachment) || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim() || (attachment ? `📎 ${attachment.name}` : ''),
      time: new Date(),
      attachment: attachment ? { mime: attachment.mime, name: attachment.name, previewUrl: attachment.previewUrl } : null,
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    const currentInput = input.trim();
    const currentAttachment = attachment;
    setInput('');
    setAttachment(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/agent/company-chat`,
        {
          message: currentInput || (currentAttachment ? (language === 'ar' ? 'حلّل هذا المرفق من فضلك' : 'Please analyze this attachment') : ''),
          history: messages.map(({ time, attachment: _a, ...m }) => m),
          ai_agent_id: task?.ai_agent_id || null,
          task_id: task?.id || taskId,
          session_id: sessionId,
          ...(currentAttachment && {
            attachment_base64: currentAttachment.base64,
            attachment_mime: currentAttachment.mime,
            attachment_name: currentAttachment.name,
          }),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || data.reply || '',
        time: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      const detail = error?.response?.data?.detail || (language === 'ar' ? 'حدث خطأ غير متوقع' : 'An unexpected error occurred');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ ${detail}`,
        time: new Date()
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // Voice recording — uses browser MediaRecorder API, then transcribes via /api/ai/transcribe
  const startRecording = async () => {
    if (isRecording || isTranscribing || loading) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 500) {
          toast.error(language === 'ar' ? 'التسجيل قصير جداً' : 'Recording too short');
          return;
        }
        await transcribeBlob(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Mic error:', err);
      toast.error(language === 'ar' ? 'تعذّر الوصول للميكروفون' : 'Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    try { mediaRecorderRef.current?.stop(); } catch (e) { /* ignore */ }
    setIsRecording(false);
  };

  const transcribeBlob = async (blob) => {
    setIsTranscribing(true);
    try {
      const token = localStorage.getItem('business_token');
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('language', language === 'ar' ? 'ar' : 'en');
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/transcribe`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = (data?.text || '').trim();
      if (text) {
        setInput(prev => (prev ? prev + ' ' : '') + text);
      } else {
        toast.error(language === 'ar' ? 'لم يُلتقط أي كلام' : 'No speech detected');
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحويل الصوت' : 'Transcription failed');
    } finally {
      setIsTranscribing(false);
      inputRef.current?.focus();
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const okTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!okTypes.includes(f.type)) {
      toast.error(language === 'ar' ? 'يُسمح بالصور و PDF فقط' : 'Only images and PDF are allowed');
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'الحجم الأقصى 50 ميجابايت' : 'Max size 50MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(',')[1] || '';
      setAttachment({
        base64: b64,
        mime: f.type,
        name: f.name,
        size: f.size,
        previewUrl: f.type.startsWith('image/') ? dataUrl : null,
      });
    };
    reader.readAsDataURL(f);
  };

  const clearAttachment = () => setAttachment(null);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitTask = async () => {
    if (submitting || messages.length < 3) {
      toast.error(
        language === 'ar'
          ? 'أكمل المحادثة أولاً قبل إرسال الطلب'
          : 'Complete the conversation before submitting'
      );
      return;
    }
    // The generic "fetch-companies" flow doesn't have a real task doc — no admin submission needed.
    if (task?.id === 'fetch-companies' || taskId === 'fetch-companies') {
      toast.success(
        language === 'ar'
          ? '✅ تم حفظ المحادثة. سيتم مراجعة البيانات وإضافة الشركة قريباً.'
          : '✅ Conversation saved. Data will be reviewed and added shortly.'
      );
      navigate('/agent-tasks');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('business_token');
      const conversationText = messages
        .map(m => `${m.role === 'user' ? 'Agent' : 'AI'}: ${m.content}`)
        .join('\n');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/agent/task-submissions`,
        {
          task_id: task?.id || taskId,
          task_title_ar: task?.title_ar,
          task_title_en: task?.title_en,
          ai_agent_id: task?.ai_agent_id,
          mode: 'ai',
          payload: {
            conversation: messages.map(({ time, ...m }) => m),
            conversation_text: conversationText,
            session_id: sessionId,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        language === 'ar'
          ? `✅ تم إرسال الطلب: ${data.request_number || ''}`
          : `✅ Submitted: ${data.request_number || ''}`
      );
      navigate('/my-requests');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        (language === 'ar' ? 'فشل إرسال الطلب' : 'Submission failed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (t) => t ? new Date(t).toLocaleTimeString(language === 'ar' ? 'ar-AE' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  const glassBubble = isDarkMode
    ? 'bg-[rgba(26,26,26,0.9)] border border-white/10 text-gray-200'
    : 'bg-white/80 border border-gray-200/70 text-gray-800';

  const taskTitle = task
    ? (language === 'ar' ? (task.title_ar || task.title) : (task.title_en || task.title_ar || task.title))
    : (language === 'ar' ? 'مهمة الوكيل' : 'Agent Task');

  // ===== Boot screen =====
  if (booting || taskLoading) {
    return (
      <div className={`h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] flex flex-col items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`} data-testid="agent-chat-boot-screen">
        <style>{chatStyles}</style>
        <img
          src={ORB}
          alt="CloudGM AI"
          className="w-24 h-24 ag-orb-float"
          style={{ filter: 'drop-shadow(0 0 30px rgba(30,155,217,0.6))' }}
        />
        <p className={`mt-6 text-sm font-semibold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} style={{ letterSpacing: '0.5em', textIndent: '0.5em' }}>
          CLOUDGM AI
        </p>
      </div>
    );
  }

  return (
    <div className={`h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] flex flex-col relative overflow-hidden ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <style>{chatStyles}</style>

      {/* Ambient background — unified CloudGM AI blue glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[400px]" style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.45) 0%, transparent 70%)', opacity: isDarkMode ? 1 : 0.4 }} />
        <div className="absolute -bottom-32 left-1/4 w-[500px] h-[350px]" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.35) 0%, transparent 70%)', opacity: isDarkMode ? 1 : 0.35 }} />
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} 1px, transparent 1px)`, backgroundSize: '32px 32px' }} />
      </div>

      {/* Header */}
      <header className={`relative z-10 py-3 px-4 backdrop-blur-xl border-b ${isDarkMode ? 'bg-black/40 border-white/10' : 'bg-white/60 border-gray-200/60'}`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate('/agent-tasks')}
              data-testid="agent-chat-back-btn"
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 ${isDarkMode ? 'border-white/15 text-gray-300 hover:bg-white/10' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
            >
              <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
            </button>

            {/* Unified CloudGM AI orb */}
            <div className="relative flex-shrink-0">
              <span className="absolute inset-0 rounded-full ag-ping-ring" style={{ background: 'rgba(30,155,217,0.4)' }} />
              <img
                src={ORB}
                alt="AI"
                className="relative w-10 h-10 rounded-full"
                style={{ filter: 'drop-shadow(0 0 12px rgba(30,155,217,0.55))' }}
              />
              <span className={`absolute bottom-0 end-0 w-3 h-3 rounded-full border-2 ${isDarkMode ? 'border-black' : 'border-white'}`} style={{ background: '#10B981' }} />
            </div>

            <div className="min-w-0">
              <h1 className="text-base font-extrabold bg-gradient-to-r from-[#1E9BD9] to-cyan-400 bg-clip-text text-transparent tracking-wide truncate" data-testid="agent-chat-task-title">
                CLOUDGM AI
              </h1>
              <p className={`text-xs flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Briefcase className="h-3 w-3 text-emerald-500" />
                <span className="truncate">{taskTitle}</span>
              </p>
            </div>
          </div>

          <button
            onClick={submitTask}
            disabled={submitting || messages.length < 3}
            data-testid="agent-chat-submit-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#1E9BD9] to-cyan-500 hover:opacity-95 disabled:opacity-40 flex-shrink-0"
            style={{ boxShadow: '0 4px 16px rgba(30,155,217,0.4)' }}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">
              {language === 'ar' ? 'إرسال الطلب' : 'Submit'}
            </span>
          </button>
        </div>
      </header>

      {/* Chat Area */}
      <div
        ref={scrollRef}
        className="relative z-10 flex-1 overflow-y-auto p-4 max-w-4xl mx-auto w-full"
      >
        <div className="space-y-5">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              data-testid={`agent-chat-msg-${msg.role}-${idx}`}
              className={`ag-msg-in flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.role === 'user' ? (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-[#1E9BD9] to-cyan-500" style={{ boxShadow: '0 4px 14px rgba(30,155,217,0.4)' }}>
                    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-white" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  </div>
                ) : (
                  <img
                    src={ORB}
                    alt="AI"
                    className="flex-shrink-0 w-8 h-8 rounded-full"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(30,155,217,0.55))' }}
                  />
                )}

                <div className="min-w-0">
                  <div
                    className={`p-3 backdrop-blur-md ${
                      msg.role === 'user'
                        ? 'rounded-2xl rounded-tr-md text-white bg-gradient-to-br from-[#1E9BD9] to-cyan-500'
                        : `rounded-2xl rounded-tl-md ${glassBubble}`
                    }`}
                    style={msg.role === 'user' ? { boxShadow: '0 6px 24px rgba(30,155,217,0.35)' } : {}}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="text-sm leading-relaxed ag-md">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <>
                        {msg.attachment && (
                          <div className="mb-2">
                            {msg.attachment.previewUrl ? (
                              <img
                                src={msg.attachment.previewUrl}
                                alt={msg.attachment.name}
                                className="max-w-full max-h-48 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs truncate">{msg.attachment.name}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {msg.content && <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>}
                      </>
                    )}
                  </div>
                  <div className={`flex items-center gap-1 mt-1 px-1 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && <Sparkles className="h-3 w-3 text-[#1E9BD9]" />}
                    <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} dir="ltr">{formatTime(msg.time)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="ag-msg-in flex justify-start">
              <div className="flex items-start gap-2 max-w-[85%]">
                <img
                  src={ORB}
                  alt="AI"
                  className="flex-shrink-0 w-8 h-8 rounded-full animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 8px rgba(30,155,217,0.55))' }}
                />
                <div className={`flex items-center gap-2 p-3 rounded-2xl rounded-tl-md backdrop-blur-md ${glassBubble}`}>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#1E9BD9] ag-think-dot" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#1E9BD9] ag-think-dot" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-[#1E9BD9] ag-think-dot" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'يفكّر...' : 'Thinking...'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="relative z-10 p-4">
        {/* Attachment preview strip */}
        {attachment && (
          <div className={`max-w-4xl mx-auto mb-2 flex items-center gap-3 rounded-2xl border p-2.5 pr-3 backdrop-blur-xl ${isDarkMode ? 'bg-white/5 border-white/15' : 'bg-white/80 border-gray-200/80'}`} data-testid="agent-chat-attachment-preview">
            {attachment.previewUrl ? (
              <img src={attachment.previewUrl} alt="preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                <FileText className={`h-6 w-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{attachment.name}</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {(attachment.size / 1024 / 1024).toFixed(2)} MB · {attachment.mime === 'application/pdf' ? 'PDF' : (language === 'ar' ? 'صورة' : 'Image')}
              </p>
            </div>
            <button
              onClick={clearAttachment}
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              data-testid="agent-chat-attachment-remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className={`max-w-4xl mx-auto flex items-center gap-2 rounded-full border px-2 py-1.5 backdrop-blur-xl ${isDarkMode ? 'bg-white/5 border-white/15' : 'bg-white/80 border-gray-200/80 shadow-sm'}`}>
          {/* Attach button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={onPickFile}
            data-testid="agent-chat-file-input"
          />
          <button
            data-testid="agent-chat-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || isRecording || isTranscribing}
            title={language === 'ar' ? 'إرفاق صورة أو PDF' : 'Attach image or PDF'}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-40 ${isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <Paperclip className="h-5 w-5" />
          </button>

          {/* Mic button */}
          <button
            data-testid="agent-chat-mic-btn"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={loading || isTranscribing}
            title={language === 'ar' ? (isRecording ? 'إيقاف التسجيل' : 'تسجيل صوتي') : (isRecording ? 'Stop' : 'Voice message')}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 ${
              isRecording
                ? 'text-white bg-red-500 animate-pulse'
                : (isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100')
            }`}
          >
            {isTranscribing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mic className="h-5 w-5" />}
          </button>

          <Input
            ref={inputRef}
            data-testid="agent-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              isRecording ? (language === 'ar' ? '🎙️ يستمع...' : '🎙️ Listening...') :
              isTranscribing ? (language === 'ar' ? 'جاري التحويل...' : 'Transcribing...') :
              (language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...')
            }
            className={`flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 ${isDarkMode ? 'text-white placeholder:text-gray-500' : ''}`}
            disabled={loading || isRecording || isTranscribing}
          />
          <button
            data-testid="agent-chat-send-btn"
            onClick={sendMessage}
            disabled={loading || isRecording || isTranscribing || (!input.trim() && !attachment)}
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white bg-gradient-to-br from-[#1E9BD9] to-cyan-500 disabled:opacity-40 transition-opacity"
            style={{ boxShadow: '0 4px 16px rgba(30,155,217,0.45)' }}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 rtl:rotate-180" />}
          </button>
        </div>
        <p className={`text-center text-xs mt-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          CLOUDGM AI
        </p>
      </div>
    </div>
  );
}
